// Onboarding first-goal submission, extracted from Onboarding.tsx (953 lines) so
// the failure paths are unit-testable without a 5-step RTL render.
//
// Bugs fixed:
//  - BUG-005: the inline code called `goalRes.json()` with NO `res.ok` check; on a
//    4xx/5xx the outer try/catch swallowed it and `finally` still advanced the
//    flow → the user's first goal was silently lost with no error shown.
//  - BUG-002: the starter-task POST was `.catch(() => {})` — a failure was
//    swallowed, leaving a goal with no starter task and no signal.
//
// The caller decides UX from the discriminated result:
//  - "goal-failed"  → goal was NOT created; show an error and DON'T advance
//    (safe to retry — no risk of a duplicate goal).
//  - "task-failed"  → the goal WAS created; advance anyway (the goal is the
//    primary artifact and re-submitting would risk a duplicate goal). The
//    missing starter task is surfaced, not silently swallowed.
//  - "ok"           → both created.

export type AuthFetch = (
  url: string,
  init?: { method?: string; body?: string }
) => Promise<{ ok: boolean; status: number; json: () => Promise<unknown> }>;

export type GoalSubmitResult =
  | { status: "ok"; goalId: string }
  | { status: "goal-failed"; error: string }
  | { status: "task-failed"; goalId: string; error: string };

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export async function submitOnboardingGoal(
  authFetch: AuthFetch,
  params: { title: string; category: string; goalText: string }
): Promise<GoalSubmitResult> {
  let goalId: string;
  try {
    const res = await authFetch("/api/goals", {
      method: "POST",
      body: JSON.stringify({ title: params.title, category: params.category }),
    });
    if (!res.ok) throw new Error(`goal create failed (HTTP ${res.status})`);
    const goal = (await res.json()) as { id?: string } | null;
    if (!goal?.id) throw new Error("goal create returned no id");
    goalId = goal.id;
  } catch (e) {
    return { status: "goal-failed", error: message(e) };
  }

  try {
    const res = await authFetch("/api/tasks", {
      method: "POST",
      body: JSON.stringify({
        title: "Define one concrete first step",
        description: `Toward: ${params.goalText}`,
        priority: 2,
        energy_level: "medium",
        estimated_minutes: 15,
        goal_id: goalId,
      }),
    });
    if (!res.ok) throw new Error(`task create failed (HTTP ${res.status})`);
  } catch (e) {
    return { status: "task-failed", goalId, error: message(e) };
  }

  return { status: "ok", goalId };
}
